import http from './api'
import { ChessGame, ChessPlayerPoints } from '../types/chess'

class ChessGamesService {
  async getRoomLeaderboard(roomId: string): Promise<ChessPlayerPoints[]> {
    try {
      const response = await http.get(`/chess-games/room/${roomId}/leaderboard`)
      // Handle both response formats: { leaderboard: [...] } or direct array
      const leaderboardData = response.data?.leaderboard || response.data
      return Array.isArray(leaderboardData) ? leaderboardData : []
    } catch (error: any) {
      // If endpoint doesn't exist (404), return empty array instead of throwing
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  }

  async getRoomGames(roomId: string): Promise<ChessGame[]> {
    const response = await http.get(`/chess-games/room/${roomId}`)
    // Handle both response formats: { games: [...] } or direct array
    const gamesData = response.data?.games || response.data
    return Array.isArray(gamesData) ? gamesData : []
  }

  async createGame(data: {
    roomId: string
    player1Id: string
    player2Id: string
    timeControl?: string
    variant?: string
    rated?: boolean
    name?: string
  }): Promise<ChessGame> {
    // Transform data to match backend expectations
    const requestData = {
      roomId: data.roomId,
      opponentId: data.player2Id, // Backend expects 'opponentId' not 'player2Id'
      timeControl: data.timeControl,
      variant: data.variant,
      rated: data.rated,
      name: data.name,
    }
    const response = await http.post('/chess-games', requestData)
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async createAIGame(data: {
    roomId: string
    stockfishLevel?: number
    timeControl?: string
    name?: string
  }): Promise<ChessGame> {
    const response = await http.post('/chess-games/ai', data)
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async makeMove(gameId: string, move: string): Promise<ChessGame> {
    const response = await http.post(`/chess-games/${gameId}/move`, { move })
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async getGame(gameId: string): Promise<ChessGame> {
    const response = await http.get(`/chess-games/${gameId}`)
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async updateGame(gameId: string, updateData: any): Promise<ChessGame> {
    const response = await http.put(`/chess-games/${gameId}`, updateData)
    return response.data
  }

  async resignGame(gameId: string): Promise<ChessGame> {
    // Backend uses propose-resign endpoint and gets playerId from auth token
    const response = await http.post(`/chess-games/${gameId}/propose-resign`)
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async offerDraw(gameId: string): Promise<ChessGame> {
    // Backend uses propose-draw endpoint and gets playerId from auth token
    const response = await http.post(`/chess-games/${gameId}/propose-draw`)
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async acceptDraw(gameId: string): Promise<ChessGame> {
    // Backend gets playerId from auth token
    const response = await http.post(`/chess-games/${gameId}/accept-draw`)
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async declineDraw(gameId: string): Promise<ChessGame> {
    // Backend uses reject-draw endpoint and gets playerId from auth token
    const response = await http.post(`/chess-games/${gameId}/reject-draw`)
    // Handle both response formats: { game: {...} } or direct object
    return response.data?.game || response.data || null
  }

  async joinGame(gameId: string, playerId: string): Promise<ChessGame> {
    const response = await http.post(`/chess-games/${gameId}/join`, { playerId })
    return response.data
  }

  async startGame(gameId: string): Promise<ChessGame> {
    const response = await http.post(`/chess-games/${gameId}/start`)
    return response.data?.game || response.data || null
  }

  async acceptResign(gameId: string): Promise<ChessGame> {
    const response = await http.post(`/chess-games/${gameId}/accept-resign`)
    return response.data?.game || response.data || null
  }

  async rejectResign(gameId: string): Promise<ChessGame> {
    const response = await http.post(`/chess-games/${gameId}/reject-resign`)
    return response.data?.game || response.data || null
  }
}

const chessGamesService = new ChessGamesService()
export default chessGamesService

